import React from 'react';
import { useLocation } from 'react-router-dom';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import DateRangeTwoToneIcon from '@mui/icons-material/DateRangeTwoTone';
import AssignmentIndTwoToneIcon from '@mui/icons-material/AssignmentIndTwoTone';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import BusinessCenterTwoToneIcon from '@mui/icons-material/BusinessCenterTwoTone';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DriveFileMove from '@mui/icons-material/DriveFileMove';
import SupervisorAccountTwoTone from '@mui/icons-material/SupervisorAccountTwoTone';
import SupervisorAccountTwoToneIcon from '@mui/icons-material/SupervisorAccountTwoTone';
import CurrencyExchangeTwoToneIcon from '@mui/icons-material/CurrencyExchangeTwoTone';
import SettingsIcon from '@mui/icons-material/Settings';
import QueryBuilderTwoToneIcon from '@mui/icons-material/QueryBuilderTwoTone';
import FolderCopyTwoToneIcon from '@mui/icons-material/FolderCopyTwoTone';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import Divider from '@mui/material/Divider';
import { NavModes, expandedMenuItemStyles } from '@/nav/utils/navigation.utils';
import tokens from '@/core/tokens.json';
import { ROUTE_NAMES, withRoot } from '@/core/routes/routeNames';
import { DashboardMenuIcon } from '@/components/Icon/CustomIcons';
import NavItem from '@/nav/components/NavItem';
import AuthorizationGate from '@/authorization/AuthorizationGate/AuthorizationGate';
import { SCOPES } from '@/authorization/authorization.constants';

interface PrimaryNavProps {
  type: NavModes;
}
function PrimaryNav({ type }: PrimaryNavProps) {
  const { pathname } = useLocation();
  const {
    DASHBOARD: { DASHBOARD },
    SCHEDULES: { SCHEDULES },
    EMPLOYEES: { EMPLOYEES },
    ADMINISTRATION: {
      EMPLOYER_REF,
      CONTRACT,
      USERS,
      RATES,
      NOTIFICATIONS,
      RECONCILIATION,
      RETRO_PAY,
    },
    REPORTS,
    ERTN,
  } = ROUTE_NAMES;
  const {
    MENU_ITEMS: {
      DASHBOARD: DASHBOARD_TOKEN,
      SCHEDULES: SCHEDULES_TOKEN,
      EMPLOYEES: EMPLOYEES_TOKEN,
      ERTN: ERTN_TOKEN,
      REPORTS: REPORTS_TOKEN,
      ADMINISTRATION: {
        title: ADMINISTRATION_TOKEN,
        items: {
          EMPLOYER_REF: EMPLOYER_REF_TOKEN,
          CONTRACT: CONTRACT_TOKEN,
          USERS: USERS_TOKEN,
          RATES: RATES_TOKEN,
          NOTIFICATIONS: NOTIFICATIONS_TOKEN,
          RECONCILIATION: RECONCILIATION_TOKEN,
          RETRO_PAY: RETRO_PAY_TOKEN,
        },
      },
    },
    MENU_STATE: { EXPANDED },
  } = tokens;

  return (
    <>
      {type === EXPANDED ? (
        <>
          <List disablePadding role="menu">
            <AuthorizationGate scopes={[SCOPES.VIEW_DEFAULT_NAVIGATION]}>
              <NavItem
                selected={pathname === withRoot(DASHBOARD)}
                to={withRoot(DASHBOARD)}
                icon={<DashboardMenuIcon aria-hidden />}
                expanded={type === EXPANDED}
              >
                {DASHBOARD_TOKEN}
              </NavItem>
              <NavItem
                selected={pathname === withRoot(SCHEDULES)}
                to={withRoot(SCHEDULES)}
                icon={
                  <DateRangeTwoToneIcon
                    aria-hidden
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
              >
                {SCHEDULES_TOKEN}
              </NavItem>
              <NavItem
                selected={pathname === withRoot(EMPLOYEES)}
                to={withRoot(EMPLOYEES)}
                icon={
                  <AssignmentIndTwoToneIcon
                    aria-hidden
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
              >
                {EMPLOYEES_TOKEN}
              </NavItem>
              <NavItem
                selected={pathname === withRoot(REPORTS.ROOT)}
                to={withRoot(REPORTS.ROOT)}
                icon={
                  <FolderCopyTwoToneIcon
                    aria-hidden
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
              >
                {REPORTS_TOKEN}
              </NavItem>
              <NavItem
                selected={pathname === withRoot(ERTN.ROOT)}
                to={withRoot(ERTN.ROOT)}
                icon={
                  <QueryBuilderTwoToneIcon
                    aria-hidden
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
              >
                {ERTN_TOKEN}
              </NavItem>
            </AuthorizationGate>
          </List>
          <Divider />
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton sx={expandedMenuItemStyles()}>
                <ListItemIcon>
                  <SettingsIcon
                    aria-hidden
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ letterSpacing: '0.15px' }} sx={{ my: 0 }}>
                  {ADMINISTRATION_TOKEN}
                </ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <List
                disablePadding
                role="menu"
                aria-label={ADMINISTRATION_TOKEN}
                sx={{ width: '100%' }}
              >
                <AuthorizationGate scopes={[SCOPES.VIEW_DEFAULT_NAVIGATION]}>
                  <NavItem
                    isPadded
                    selected={pathname === withRoot(CONTRACT)}
                    to={withRoot(CONTRACT)}
                    icon={
                      <DriveFileRenameOutlineOutlinedIcon
                        sx={(theme) => ({ color: theme.palette.text.primary })}
                      />
                    }
                  >
                    {CONTRACT_TOKEN}
                  </NavItem>
                  <NavItem
                    isPadded
                    selected={pathname === withRoot(EMPLOYER_REF)}
                    to={withRoot(EMPLOYER_REF)}
                    icon={
                      <BusinessCenterTwoToneIcon
                        aria-hidden
                        sx={(theme) => ({ color: theme.palette.text.primary })}
                      />
                    }
                  >
                    {EMPLOYER_REF_TOKEN}
                  </NavItem>
                  <AuthorizationGate scopes={[SCOPES.VIEW_USERS]}>
                    <NavItem
                      isPadded
                      selected={pathname === withRoot(USERS)}
                      to={withRoot(USERS)}
                      icon={
                        <SupervisorAccountTwoTone
                          aria-hidden
                          sx={(theme) => ({ color: theme.palette.text.primary })}
                        />
                      }
                    >
                      {USERS_TOKEN}
                    </NavItem>
                  </AuthorizationGate>
                </AuthorizationGate>
                <NavItem
                  isPadded
                  selected={pathname === withRoot(RATES.ROOT)}
                  to={withRoot(RATES.ROOT)}
                  icon={
                    <CurrencyExchangeTwoToneIcon
                      aria-hidden
                      sx={(theme) => ({ color: theme.palette.text.primary })}
                    />
                  }
                >
                  {RATES_TOKEN}
                </NavItem>
                <AuthorizationGate scopes={[SCOPES.VIEW_NOTIFICATIONS]}>
                  <NavItem
                    isPadded
                    selected={pathname === withRoot(NOTIFICATIONS)}
                    to={withRoot(NOTIFICATIONS)}
                    icon={
                      <NotificationsIcon
                        aria-hidden
                        sx={(theme) => ({ color: theme.palette.text.primary })}
                      />
                    }
                  >
                    {NOTIFICATIONS_TOKEN}
                  </NavItem>
                </AuthorizationGate>
                <AuthorizationGate scopes={[SCOPES.VIEW_RECONCILIATIONS]}>
                  <NavItem
                    isPadded
                    selected={pathname === withRoot(RECONCILIATION)}
                    to={withRoot(RECONCILIATION)}
                    icon={
                      <DriveFileMove
                        aria-hidden
                        sx={(theme) => ({ color: theme.palette.text.primary })}
                      />
                    }
                  >
                    {RECONCILIATION_TOKEN}
                  </NavItem>
                </AuthorizationGate>
                <AuthorizationGate scopes={[SCOPES.VIEW_RETRO_PAYS]}>
                  <NavItem
                    isPadded
                    selected={pathname === withRoot(RETRO_PAY)}
                    to={withRoot(RETRO_PAY)}
                    icon={
                      <AutoFixHigh
                        aria-hidden
                        sx={(theme) => ({ color: theme.palette.text.primary })}
                      />
                    }
                  >
                    {RETRO_PAY_TOKEN}
                  </NavItem>
                </AuthorizationGate>
              </List>
            </ListItem>
          </List>
        </>
      ) : (
        <>
          <List disablePadding role="menu">
            <AuthorizationGate scopes={[SCOPES.VIEW_DEFAULT_NAVIGATION]}>
              <NavItem
                to={withRoot(DASHBOARD)}
                selected={pathname === withRoot(DASHBOARD)}
                icon={<DashboardMenuIcon titleAccess={tokens.DASHBOARD} />}
                expanded={false}
              ></NavItem>
              <NavItem
                to={withRoot(SCHEDULES)}
                selected={pathname === withRoot(SCHEDULES)}
                icon={
                  <DateRangeTwoToneIcon
                    titleAccess={tokens.SCHEDULES}
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
                expanded={false}
              ></NavItem>
              <NavItem
                to={withRoot(EMPLOYEES)}
                selected={pathname === withRoot(EMPLOYEES)}
                icon={
                  <AssignmentIndTwoToneIcon
                    titleAccess={tokens.EMPLOYEES}
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
                expanded={false}
              ></NavItem>
              <NavItem
                to={withRoot(REPORTS.ROOT)}
                selected={pathname === withRoot(REPORTS.ROOT)}
                icon={
                  <FolderCopyTwoToneIcon
                    aria-hidden
                    titleAccess={REPORTS_TOKEN}
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
                expanded={false}
              >
                {REPORTS_TOKEN}
              </NavItem>
              <NavItem
                to={withRoot(ERTN.ROOT)}
                selected={pathname === withRoot(ERTN.ROOT)}
                icon={
                  <QueryBuilderTwoToneIcon
                    aria-hidden
                    titleAccess={ERTN_TOKEN}
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
                expanded={false}
              >
                {ERTN_TOKEN}
              </NavItem>
            </AuthorizationGate>
          </List>
          <Divider />
          <List disablePadding role="menu">
            <AuthorizationGate scopes={[SCOPES.VIEW_DEFAULT_NAVIGATION]}>
              <NavItem
                to={withRoot(CONTRACT)}
                selected={pathname === withRoot(CONTRACT)}
                icon={
                  <DriveFileRenameOutlineOutlinedIcon
                    titleAccess={tokens.CONTRACT}
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
                expanded={false}
              ></NavItem>
              <NavItem
                to={withRoot(EMPLOYER_REF)}
                selected={pathname === withRoot(EMPLOYER_REF)}
                icon={
                  <BusinessCenterTwoToneIcon
                    titleAccess={EMPLOYER_REF_TOKEN}
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
                expanded={false}
              ></NavItem>
              <AuthorizationGate scopes={[SCOPES.VIEW_USERS]}>
                <NavItem
                  to={withRoot(USERS)}
                  selected={pathname === withRoot(USERS)}
                  icon={
                    <SupervisorAccountTwoToneIcon
                      titleAccess={USERS_TOKEN}
                      sx={(theme) => ({ color: theme.palette.text.primary })}
                    />
                  }
                  expanded={false}
                ></NavItem>
              </AuthorizationGate>
            </AuthorizationGate>
            <NavItem
              to={withRoot(RATES.ROOT)}
              selected={pathname === withRoot(RATES.ROOT)}
              icon={
                <CurrencyExchangeTwoToneIcon
                  titleAccess={RATES_TOKEN}
                  sx={(theme) => ({ color: theme.palette.text.primary })}
                />
              }
              expanded={false}
            ></NavItem>
            <AuthorizationGate scopes={[SCOPES.VIEW_NOTIFICATIONS]}>
              <NavItem
                to={withRoot(NOTIFICATIONS)}
                selected={pathname === withRoot(NOTIFICATIONS)}
                icon={
                  <NotificationsIcon
                    titleAccess={NOTIFICATIONS_TOKEN}
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
                expanded={false}
              ></NavItem>
            </AuthorizationGate>
            <AuthorizationGate scopes={[SCOPES.VIEW_RECONCILIATIONS]}>
              <NavItem
                to={withRoot(RECONCILIATION)}
                selected={pathname === withRoot(RECONCILIATION)}
                icon={
                  <DriveFileMove
                    titleAccess={RECONCILIATION_TOKEN}
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
                expanded={false}
              ></NavItem>
            </AuthorizationGate>
            <AuthorizationGate scopes={[SCOPES.VIEW_RETRO_PAYS]}>
              <NavItem
                to={withRoot(RETRO_PAY)}
                selected={pathname === withRoot(RETRO_PAY)}
                icon={
                  <AutoFixHigh
                    titleAccess={RETRO_PAY_TOKEN}
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                }
                expanded={false}
              ></NavItem>
            </AuthorizationGate>
          </List>
        </>
      )}
    </>
  );
}

export default PrimaryNav;
